import React from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import Icon from '../shared/icon'
import { useCatalogue } from './CatalogueProvider'
import { categoriesOf } from './catalogueSelectors'
import { MAIN_DROPPABLE_ID, moveItemAndRenumberPositions } from './catalogueOrdering'
import { CategoryNode } from './catalogueTypes'
import { t } from '../../i18n'

const CategorySidebar = () => {
  const { state, actions, meta } = useCatalogue()
  const { roots, editMode, expandedRootId, selectedCategory, categoryId } = state

  const toggleExpansion = (root: CategoryNode) =>
    actions.setSidebarOverride({ forCategoryId: categoryId, expandedRootId: expandedRootId === root.id ? null : root.id })

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    actions.reorderRoots(moveItemAndRenumberPositions(roots, result.source.index, result.destination.index))
  }

  const selectedClass = (category: CategoryNode) => (selectedCategory?.id === category.id ? 'selected-item' : '')

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={MAIN_DROPPABLE_ID} direction="vertical">
        {(droppable) => (
          <div {...droppable.droppableProps} ref={droppable.innerRef}>
            <div className="robis-sidebar">
              {roots.map((category, index) => (
                <React.Fragment key={category.id}>
                  <Draggable draggableId={`drag-${category.id}`} index={index} isDragDisabled={!editMode}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`d-flex justify-content-between align-items-center sidebar-item pointer ${
                          snapshot.isDragging ? 'dragging-row' : ''
                        } ${selectedClass(category)}`}
                        onClick={() => meta.go.category(category.id)}
                      >
                        <div className="sidebar-content">
                          {editMode ? <Icon name="dragBurger" className="me-2 drag-icon" /> : null}
                          <b className="as-link">{category.name}</b>
                        </div>
                        {category.children.length > 0 ? (
                          <button
                            type="button"
                            className="sidebar-caret"
                            aria-expanded={expandedRootId === category.id}
                            aria-label={t('catalogue.toggle_subcategories')}
                            onClick={(event) => {
                              event.stopPropagation()
                              toggleExpansion(category)
                            }}
                          >
                            <Icon name={expandedRootId === category.id ? 'arrowUp' : 'arrowDown'} />
                          </button>
                        ) : null}
                      </div>
                    )}
                  </Draggable>
                  {expandedRootId === category.id
                    ? categoriesOf(category.children).map((child) => (
                        <div
                          className={`robis-nested pointer ${selectedClass(child)}`}
                          key={child.id}
                          onClick={(event) => {
                            event.stopPropagation()
                            meta.go.category(child.id)
                          }}
                        >
                          {child.name}
                        </div>
                      ))
                    : null}
                </React.Fragment>
              ))}
            </div>
            {droppable.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default CategorySidebar
